import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Database, RefreshCw, CheckCircle, AlertCircle, Info, TrendingUp, Download, LogOut, UserPlus, Users, Key, FileText, DollarSign, Settings, Link2, ChevronDown, ChevronRight, Home, LayoutGrid, Trash2, AlertTriangle, X, Mail, Clock, ArrowRight } from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import AdminLogin from '../components/AdminLogin';
import { useAuth } from '../contexts/AuthContext';
import { fetchStageConfig, STAGE_DISPLAY } from '../constants/applicationStages';
import WorkflowConfigEditor from '../components/WorkflowConfigEditor';
import {
  resetPassword,
  getAdminLeads,
  getLeadStats,
  exportLeadsCsv,
  updateAdminLeadStatus,
  batchBillLeads,
  setMasterBroker,
  setLeadPrice,
  getAdminConfig,
  getAdminBrokers,
  updateAdminBrokerProfile,
  getAdminConnections,
  updateAdminConnectionStatus,
  deleteAdminConnection,
  getWorkflowStatus,
  toggleWorkflowFlow,
  triggerStaleCheck,
  triggerExpiryCheck,
  triggerEmailSend,
  listAllApplications,
  deleteApplication,
  clearAllLeads,
  deleteLead,
  getConnectionHistory,
  deleteCustomer,
  listCustomers,
  type Lead,
  type LeadStats,
  type AdminConfig,
  type Broker,
  type AdminConnection,
  type WorkflowStatus,
  type Application,
  type ConnectionHistory,
  type AdminCustomer,
} from '../api/brokerApi';
import { authenticatedFetch } from '../api/authApi';

interface ScraperStatus {
  status: string;
  available_scrapers: string[];
  last_scrape: {
    timestamp: string;
    institution: string;
    source_url: string;
  } | null;
}

interface ScrapeResult {
  success: boolean;
  institution_code: string;
  products_found: number;
  products_valid: number;
  products_invalid: number;
  quality_score: number;
  validation_summary: any;
  products: any[];
  message: string;
}

interface ScrapedProduct {
  id: string;
  product_name: string;
  product_type: string;
  interest_rate: number;
  comparison_rate: number;
  source: string;
  source_url: string;
  last_scraped_at: string;
  data_quality_score: number;
}

interface BrokerApproval {
  id: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_at: string | null;
  created_at: string | null;
}

const AdminPortal: React.FC = () => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) return <AdminLogin />;
  if (user?.userType === 'data_operator') return <Navigate to="/dataentry" replace />;
  if (user?.userType !== 'admin') return <AdminLogin />;

  return <AdminPortalContent />;
};

const AdminPortalContent: React.FC = () => {
  const navigate = useNavigate();
  const { logout, getAccessToken } = useAuth();
  const [status, setStatus] = useState<ScraperStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [scrapeResult, setScrapeResult] = useState<ScrapeResult | null>(null);
  const [scrapedProducts, setScrapedProducts] = useState<ScrapedProduct[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<string>('CBA');
  const [useSampleData, setUseSampleData] = useState(true);
  const [saveToDatabase, setSaveToDatabase] = useState(false);

  const [brokerApprovals, setBrokerApprovals] = useState<BrokerApproval[]>([]);
  const [brokerEmail, setBrokerEmail] = useState('');
  const [brokerLoading, setBrokerLoading] = useState(false);

  // Password reset state
  const [resetUser, setResetUser] = useState('admin');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Lead management state
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadStats, setLeadStats] = useState<LeadStats | null>(null);
  const [leadFilter, setLeadFilter] = useState('');
  const [leadDateFrom, setLeadDateFrom] = useState('');
  const [leadDateTo, setLeadDateTo] = useState('');
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsPage, setLeadsPage] = useState(0);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());
  const LEADS_PER_PAGE = 10;

  // Admin config state
  const [adminConfig, setAdminConfig] = useState<AdminConfig | null>(null);
  const [brokerList, setBrokerList] = useState<Broker[]>([]);
  const [configLeadPrice, setConfigLeadPrice] = useState('');
  const [configMasterBroker, setConfigMasterBroker] = useState('');
  const [configSaving, setConfigSaving] = useState(false);

  // Broker profile edit state
  const [editingBrokerId, setEditingBrokerId] = useState<string | null>(null);
  const [editBrokerForm, setEditBrokerForm] = useState<{
    business_name: string; license_number: string; years_experience: string;
    specialization: string; bio: string; is_accepting_clients: boolean; is_master_broker: boolean; can_add_customers: boolean;
  }>({ business_name: '', license_number: '', years_experience: '', specialization: '', bio: '', is_accepting_clients: true, is_master_broker: false, can_add_customers: false });
  const [brokerSaving, setBrokerSaving] = useState(false);

  // Connections state
  const [adminConnections, setAdminConnections] = useState<AdminConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionStatusFilter, setConnectionStatusFilter] = useState<string>('all');
  const [selectedConnectionHistory, setSelectedConnectionHistory] = useState<ConnectionHistory | null>(null);
  const [connectionHistoryLoading, setConnectionHistoryLoading] = useState(false);
  const connectionHistoryRef = useRef<HTMLDivElement>(null);

  // Tab navigation
  const [activeTab, setActiveTab] = useState<'overview' | 'workflow' | 'brokers' | 'scraping' | 'leads' | 'applications' | 'customers' | 'billing'>('overview');

  // Applications management state (admin)
  const [adminApps, setAdminApps] = useState<Application[]>([]);
  const [adminAppsLoading, setAdminAppsLoading] = useState(false);
  const [adminAppSearch, setAdminAppSearch] = useState('');
  const [deleteConfirmApp, setDeleteConfirmApp] = useState<Application | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteCustomerConfirm, setDeleteCustomerConfirm] = useState<{ id: string; name: string; email: string } | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);

  // Customers master list state
  const [adminCustomers, setAdminCustomers] = useState<AdminCustomer[]>([]);
  const [adminCustomersLoading, setAdminCustomersLoading] = useState(false);
  const [adminCustomerSearch, setAdminCustomerSearch] = useState('');

  // Workflow automation state (Epic 3B)
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus | null>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowToggling, setWorkflowToggling] = useState<string | null>(null);

  const loadWorkflowStatus = async () => {
    const token = getAccessToken();
    if (!token) return;
    setWorkflowLoading(true);
    try {
      const data = await getWorkflowStatus(token);
      setWorkflowStatus(data);
    } catch {
      setWorkflowStatus(null);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleToggleFlow = async (flowId: string, currentDisabled: boolean) => {
    const token = getAccessToken();
    if (!token) return;
    setWorkflowToggling(flowId);
    try {
      await toggleWorkflowFlow(token, flowId, !currentDisabled);
      loadWorkflowStatus();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle flow');
    } finally {
      setWorkflowToggling(null);
    }
  };

  const API_BASE = import.meta.env.VITE_DATA_SCRAPER_URL
    || (import.meta.env.DEV ? 'http://localhost:8001' : 'http://moneybridge-feature-data-scraper.australiaeast.azurecontainer.io:8001');

  const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
    ? ''
    : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000');

  const loadBrokerApprovals = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/broker-approvals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBrokerApprovals(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load broker approvals:', err);
    }
  };

  const addBrokerApproval = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brokerEmail.trim()) return;
    const token = getAccessToken();
    if (!token) return;
    setBrokerLoading(true);
    try {
      const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/broker-approvals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: brokerEmail.trim() }),
      });
      if (res.ok) {
        setBrokerEmail('');
        loadBrokerApprovals();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to add broker');
      }
    } catch {
      alert('Failed to add broker');
    } finally {
      setBrokerLoading(false);
    }
  };

  const updateBrokerStatus = async (id: string, status: 'approved' | 'rejected' | 'pending') => {
    const token = getAccessToken();
    if (!token) return;
    setBrokerLoading(true);
    try {
      const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/broker-approvals/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (res.ok) loadBrokerApprovals();
      else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to update');
      }
    } catch {
      alert('Failed to update');
    } finally {
      setBrokerLoading(false);
    }
  };

  // ── Password Reset ──────────────────────────────────────────────────────────
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMsg('');
    setResetError('');
    if (newPassword !== confirmPassword) { setResetError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setResetError('Password must be at least 8 characters'); return; }
    const token = getAccessToken();
    if (!token) return;
    setResetLoading(true);
    try {
      await resetPassword(token, resetUser, newPassword);
      setResetMsg(`Password for "${resetUser}" has been reset successfully.`);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setResetError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  // ── Lead Management ────────────────────────────────────────────────────────
  const loadLeads = async () => {
    const token = getAccessToken();
    if (!token) return;
    setLeadsLoading(true);
    try {
      const filters: { status?: string; date_from?: string; date_to?: string } = {};
      if (leadFilter) filters.status = leadFilter;
      if (leadDateFrom) filters.date_from = leadDateFrom;
      if (leadDateTo) filters.date_to = leadDateTo;
      const [leadsRes, statsRes] = await Promise.all([
        getAdminLeads(token, Object.keys(filters).length > 0 ? filters : undefined),
        getLeadStats(token),
      ]);
      setLeads(leadsRes.items || []);
      setLeadStats(statsRes);
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleExportCsv = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const blob = await exportLeadsCsv(token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  const handleLeadStatusUpdate = async (id: string, newStatus: string) => {
    const token = getAccessToken();
    if (!token) return;
    try {
      await updateAdminLeadStatus(token, id, newStatus);
      loadLeads();
    } catch (err) {
      console.error('Failed to update lead status:', err);
    }
  };

  const handleBatchBill = async () => {
    const token = getAccessToken();
    if (!token) { alert('Not authenticated — please log in again'); return; }
    const billable = leads.filter(l => !l.is_billed && ['contacted', 'qualified', 'converted'].includes(l.status));
    if (billable.length === 0) {
      alert('No billable leads found. Only leads with status contacted, qualified, or converted can be billed.');
      return;
    }
    if (!confirm(`Bill ${billable.length} lead(s)? This will mark them as billed.`)) return;
    try {
      const result = await batchBillLeads(token, billable.map(l => l.id));
      alert(`Successfully billed ${result.billed_count} lead(s)`);
      loadLeads();
    } catch (err) {
      alert(`Billing failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Batch bill failed:', err);
    }
  };

  const handleClearAllLeads = async () => {
    const token = getAccessToken();
    if (!token) return;
    if (!confirm(`WARNING: This will permanently delete ALL ${leads.length} leads. This action cannot be undone.\n\nAre you sure?`)) return;
    if (!confirm('This is your final confirmation. All lead data will be permanently deleted.')) return;
    try {
      await clearAllLeads(token);
      alert('All leads cleared successfully');
      loadLeads();
    } catch (err) {
      alert(`Failed to clear leads: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Clear leads failed:', err);
    }
  };

  const handleDeleteLead = async (id: string, name: string) => {
    const token = getAccessToken();
    if (!token) return;
    if (!confirm(`Delete lead "${name || 'unnamed'}"? This cannot be undone.`)) return;
    try {
      await deleteLead(token, id);
      loadLeads();
    } catch (err) {
      alert(`Failed to delete lead: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // ── Admin Config ───────────────────────────────────────────────────────────
  const loadAdminConfig = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const [config, brokersRes] = await Promise.all([
        getAdminConfig(token),
        getAdminBrokers(token),
      ]);
      setAdminConfig(config);
      setBrokerList(brokersRes.brokers || []);
      setConfigLeadPrice(config.lead_price_aud || '50');
      setConfigMasterBroker(config.master_broker_id || '');
    } catch (err) {
      console.error('Failed to load admin config:', err);
    }
  };

  const loadAdminConnections = async () => {
    const token = getAccessToken();
    if (!token) return;
    setConnectionsLoading(true);
    try {
      const data = await getAdminConnections(token);
      setAdminConnections(data.connections || []);
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setConnectionsLoading(false);
    }
  };

  const loadConnectionHistory = async (connectionId: string) => {
    const token = getAccessToken();
    if (!token) return;
    setConnectionHistoryLoading(true);
    try {
      const data = await getConnectionHistory(token, connectionId);
      setSelectedConnectionHistory(data);
      setTimeout(() => connectionHistoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      console.error('Failed to load connection history:', err);
    } finally {
      setConnectionHistoryLoading(false);
    }
  };

  const handleSaveMasterBroker = async () => {
    const token = getAccessToken();
    if (!token || !configMasterBroker) return;
    setConfigSaving(true);
    try {
      await setMasterBroker(token, configMasterBroker);
      loadAdminConfig();
    } catch (err) {
      console.error('Failed to set master broker:', err);
    } finally {
      setConfigSaving(false);
    }
  };

  const handleSaveLeadPrice = async () => {
    const token = getAccessToken();
    if (!token) return;
    setConfigSaving(true);
    try {
      await setLeadPrice(token, configLeadPrice);
      loadAdminConfig();
    } catch (err) {
      console.error('Failed to set lead price:', err);
    } finally {
      setConfigSaving(false);
    }
  };

  const startEditBroker = (b: Broker) => {
    setEditingBrokerId(b.id);
    // Handle both UK (specialisation) and US (specialization) spelling from API
    const spec = b.specialisation || (b as unknown as Record<string, unknown>)['specialization'] as string || '';
    setEditBrokerForm({
      business_name: b.business_name || '',
      license_number: b.license_number || '',
      years_experience: String(b.years_experience || ''),
      specialization: spec,
      bio: b.bio || '',
      is_accepting_clients: b.is_accepting_clients ?? true,
      is_master_broker: b.is_master_broker ?? false,
      can_add_customers: b.can_add_customers ?? false,
    });
  };

  const handleSaveBrokerProfile = async () => {
    const token = getAccessToken();
    if (!token || !editingBrokerId) return;
    setBrokerSaving(true);
    try {
      await updateAdminBrokerProfile(token, editingBrokerId, {
        business_name: editBrokerForm.business_name || undefined,
        license_number: editBrokerForm.license_number || undefined,
        years_experience: editBrokerForm.years_experience ? parseInt(editBrokerForm.years_experience) : undefined,
        specialization: editBrokerForm.specialization || undefined,
        bio: editBrokerForm.bio || undefined,
        is_accepting_clients: editBrokerForm.is_accepting_clients,
        is_master_broker: editBrokerForm.is_master_broker,
        can_add_customers: editBrokerForm.can_add_customers,
      });
      setEditingBrokerId(null);
      loadAdminConfig();
    } catch (err) {
      console.error('Failed to update broker profile:', err);
      alert('Failed to update broker profile');
    } finally {
      setBrokerSaving(false);
    }
  };

  const loadStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${API_BASE}/status`, { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Failed to load status:', error);
      setStatus({ status: 'offline', available_scrapers: [], last_scrape: null });
    }
  };

  const loadScrapedProducts = async () => {
    try {
      const response = await fetch(`${API_BASE}/products/scraped?limit=20`);
      const data = await response.json();
      setScrapedProducts(data.products || []);
    } catch (error) {
      console.error('Failed to load scraped products:', error);
    }
  };

  useEffect(() => {
    loadStatus();
    loadScrapedProducts();
  }, []);

  useEffect(() => {
    loadBrokerApprovals();
    loadLeads();
    loadAdminConfig();
    loadAdminConnections();
    loadWorkflowStatus();
    const token = getAccessToken();
    if (token) fetchStageConfig(token).catch(() => {});
  }, []);

  useEffect(() => {
    loadLeads();
  }, [leadFilter, leadDateFrom, leadDateTo]);

  // Auto-load applications when the Applications tab is selected
  const loadAdminApps = (search?: string) => {
    const token = getAccessToken();
    if (!token) {
      console.warn('No access token — admin must log in via API (not demo fallback) to view applications');
      return;
    }
    setAdminAppsLoading(true);
    listAllApplications(token, { search: search || undefined })
      .then(res => setAdminApps(res.applications || []))
      .catch((err) => console.error('Failed to load admin applications:', err))
      .finally(() => setAdminAppsLoading(false));
  };

  useEffect(() => {
    if (activeTab === 'applications' && adminApps.length === 0 && !adminAppsLoading) {
      loadAdminApps();
    }
  }, [activeTab]);

  const loadAdminCustomers = (search?: string) => {
    const token = getAccessToken();
    if (!token) return;
    setAdminCustomersLoading(true);
    listCustomers(token, search || undefined)
      .then(res => setAdminCustomers(res.customers || []))
      .catch((err) => console.error('Failed to load customers:', err))
      .finally(() => setAdminCustomersLoading(false));
  };

  useEffect(() => {
    if (activeTab === 'customers' && adminCustomers.length === 0 && !adminCustomersLoading) {
      loadAdminCustomers();
    }
  }, [activeTab]);

  const handleScrape = async () => {
    setLoading(true);
    setScrapeResult(null);

    // Warm up scraper on cold start (fire-and-forget)
    try { fetch(`${API_BASE}/status`).catch(() => {}); } catch {}

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout for scraper
      const response = await fetch(`${API_BASE}/scrape/${selectedInstitution}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          institution_code: selectedInstitution,
          use_sample_data: useSampleData,
          save_to_database: saveToDatabase,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      setScrapeResult(data);

      if (data.success && saveToDatabase) {
        // Reload scraped products
        setTimeout(loadScrapedProducts, 1000);
      }
    } catch (error) {
      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      console.error('Scrape failed:', error);
      setScrapeResult({
        success: false,
        institution_code: selectedInstitution,
        products_found: 0,
        products_valid: 0,
        products_invalid: 0,
        quality_score: 0,
        validation_summary: {},
        products: [],
        message: isTimeout
          ? 'Scraper is waking up (cold start). Please wait a moment and try again.'
          : `Error: ${error}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BrandLogo showTagline={false} size="sm" linkToHome />
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <div className="flex items-center gap-3">
                <Database className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
                  <p className="text-sm text-gray-600">Data Source Management</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {status === null ? (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-500 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Loading…
                </span>
              ) : (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  status.status === 'operational'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {status.status}
                </span>
              )}
              <button
                onClick={() => navigate('/dataentry')}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-teal-300 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors"
                title="Data Entry Portal"
              >
                Data Entry
              </button>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="flex gap-6 -mb-px">
            {([
              { id: 'overview' as const, label: 'Overview', icon: Home },
              { id: 'workflow' as const, label: 'Workflow Config', icon: Settings },
              { id: 'brokers' as const, label: 'Brokers', icon: Users },
              { id: 'scraping' as const, label: 'Data & Scraping', icon: Database },
              { id: 'leads' as const, label: 'Leads', icon: FileText },
              { id: 'applications' as const, label: 'Applications', icon: LayoutGrid },
              { id: 'customers' as const, label: 'Customers', icon: UserPlus },
              { id: 'billing' as const, label: 'Billing', icon: DollarSign },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === 'overview' && (<>
        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Database className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Available Scrapers</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {status?.available_scrapers.length || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {status?.available_scrapers.join(', ') || 'None'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">Scraped Products</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">{scrapedProducts.length}</p>
            <p className="text-sm text-gray-600 mt-1">In database</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Last Scrape</h3>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {status?.last_scrape 
                ? new Date(status.last_scrape.timestamp).toLocaleDateString() 
                : 'Never'}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {status?.last_scrape?.source_url ? 'Data available' : 'No data yet'}
            </p>
          </div>
        </div>

        {/* Workflow Automation (Epic 3B) */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              Workflow Automation
            </h2>
            <button
              onClick={loadWorkflowStatus}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${workflowLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {!workflowStatus ? (
            <div className="text-sm text-gray-500">
              {workflowLoading ? 'Loading workflow status...' : 'Node-RED workflow engine unavailable'}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${
                  workflowStatus.health.status === 'healthy'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {workflowStatus.health.status === 'healthy' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  Node-RED: {workflowStatus.health.status}
                </span>
              </div>

              <div className="space-y-2">
                {workflowStatus.flows.map((flow) => (
                  <div
                    key={flow.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      flow.disabled ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{flow.label}</p>
                      <p className="text-xs text-gray-500">{flow.info || 'Automation flow'}</p>
                    </div>
                    <button
                      onClick={() => handleToggleFlow(flow.id, flow.disabled)}
                      disabled={workflowToggling === flow.id}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        flow.disabled ? 'bg-gray-300' : 'bg-green-600'
                      } ${workflowToggling === flow.id ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          flow.disabled ? 'translate-x-1' : 'translate-x-6'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        </>)}

        {/* ═══ WORKFLOW CONFIG TAB ═══ */}
        {activeTab === 'workflow' && (<>
        {/* Manual Trigger Buttons */}
        <div style={{ marginBottom: 24, padding: 16, background: '#f0f4ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#1a2744' }}>Email System Controls</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                try {
                  const r = await triggerEmailSend(getAccessToken()!);
                  alert(r.message);
                } catch (e: unknown) { alert('Error: ' + (e instanceof Error ? e.message : String(e))); }
              }}
              style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              Process Email Queue Now
            </button>
            <button
              onClick={async () => {
                try {
                  const r = await triggerStaleCheck(getAccessToken()!);
                  alert(r.message);
                } catch (e: unknown) { alert('Error: ' + (e instanceof Error ? e.message : String(e))); }
              }}
              style={{ padding: '8px 16px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              Run Stale App Check
            </button>
            <button
              onClick={async () => {
                try {
                  const r = await triggerExpiryCheck(getAccessToken()!);
                  alert(r.message);
                } catch (e: unknown) { alert('Error: ' + (e instanceof Error ? e.message : String(e))); }
              }}
              style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              Run Pre-Approval Expiry Check
            </button>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280' }}>
            These run automatically (email queue: every 2 min, stale: daily 8am AEST, expiry: daily 9am AEST).
            Use these buttons to trigger them manually for testing.
          </p>
        </div>
        <WorkflowConfigEditor token={getAccessToken()!} />
        </>)}

        {/* ═══ DATA & SCRAPING TAB ═══ */}
        {activeTab === 'scraping' && (<>
        {/* Scraping Control Panel */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Trigger Data Scrape</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Institution
              </label>
              <select
                value={selectedInstitution}
                onChange={(e) => setSelectedInstitution(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {status?.available_scrapers.map((scraper) => (
                  <option key={scraper} value={scraper}>
                    {scraper}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={useSampleData}
                  onChange={(e) => setUseSampleData(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Use Sample Data (Demo Mode)
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={saveToDatabase}
                  onChange={(e) => setSaveToDatabase(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Save to Database
                </span>
              </label>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Ethical Notice</p>
                <p>
                  Web scraping should respect robots.txt and terms of service. 
                  Live scraping is for demonstration only. Our primary data strategy is the 
                  FI Portal where lenders manage their own products.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleScrape}
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Scraping...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Start Scrape
              </>
            )}
          </button>
        </div>

        </>)}

        {/* ═══ BROKERS TAB ═══ */}
        {activeTab === 'brokers' && (<>
        {/* Broker Approvals */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Broker Approvals
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Add broker emails to pending. Approved brokers can sign in with OAuth (Google/Microsoft/Apple) as broker.
          </p>
          <form onSubmit={addBrokerApproval} className="flex gap-2 mb-6">
            <input
              type="email"
              value={brokerEmail}
              onChange={(e) => setBrokerEmail(e.target.value)}
              placeholder="broker@example.com"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={brokerLoading || !brokerEmail.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <UserPlus size={18} />
              Add to Pending
            </button>
          </form>
          <div className="flex gap-4 mb-4">
            <button onClick={loadBrokerApprovals} className="text-indigo-600 hover:text-indigo-700 flex items-center gap-2">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {brokerApprovals.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">No broker approvals yet</td></tr>
                ) : (
                  brokerApprovals.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.status === 'approved' ? 'bg-green-100 text-green-800' :
                          item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.status === 'pending' && (
                          <span className="flex gap-2">
                            <button
                              onClick={() => updateBrokerStatus(item.id, 'approved')}
                              disabled={brokerLoading}
                              className="text-green-600 hover:text-green-700 text-sm font-medium disabled:opacity-50"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => updateBrokerStatus(item.id, 'rejected')}
                              disabled={brokerLoading}
                              className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </span>
                        )}
                        {item.status === 'approved' && (
                          <button
                            onClick={() => updateBrokerStatus(item.id, 'pending')}
                            disabled={brokerLoading}
                            className="text-gray-600 hover:text-gray-700 text-sm font-medium disabled:opacity-50"
                          >
                            Revoke
                          </button>
                        )}
                        {item.status === 'rejected' && (
                          <button
                            onClick={() => updateBrokerStatus(item.id, 'pending')}
                            disabled={brokerLoading}
                            className="text-gray-600 hover:text-gray-700 text-sm font-medium disabled:opacity-50"
                          >
                            Reset to Pending
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Broker Profiles */}
        {brokerList.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Broker Profiles
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              View and edit registered broker profiles.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Business Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">License</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accepting</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Master</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {brokerList.map((b) => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{b.business_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{b.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{b.license_number || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{b.years_experience ? `${b.years_experience} yrs` : '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${b.is_accepting_clients ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {b.is_accepting_clients ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {b.is_master_broker && <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">Master</span>}
                        {b.can_add_customers && <span className="ml-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Can Add</span>}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => startEditBroker(b)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Edit Broker Modal */}
            {editingBrokerId && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Edit Broker Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                    <input
                      type="text"
                      value={editBrokerForm.business_name}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, business_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                    <input
                      type="text"
                      value={editBrokerForm.license_number}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, license_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Years Experience</label>
                    <input
                      type="number"
                      value={editBrokerForm.years_experience}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, years_experience: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                    <input
                      type="text"
                      value={editBrokerForm.specialization}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, specialization: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="e.g. First Home, Investment, Refinance"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={editBrokerForm.bio}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, bio: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editBrokerForm.is_accepting_clients}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, is_accepting_clients: e.target.checked })}
                      id="accepting-clients"
                      className="rounded"
                    />
                    <label htmlFor="accepting-clients" className="text-sm text-gray-700">Accepting new clients</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editBrokerForm.is_master_broker}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, is_master_broker: e.target.checked })}
                      id="master-broker"
                      className="rounded"
                    />
                    <label htmlFor="master-broker" className="text-sm text-gray-700">Master Broker <span className="text-xs text-gray-400">(only one — auto-unsets others)</span></label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editBrokerForm.can_add_customers}
                      onChange={(e) => setEditBrokerForm({ ...editBrokerForm, can_add_customers: e.target.checked })}
                      id="can-add-customers"
                      className="rounded"
                    />
                    <label htmlFor="can-add-customers" className="text-sm text-gray-700">Can Add Customers <span className="text-xs text-gray-400">(broker-initiated onboarding)</span></label>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleSaveBrokerProfile}
                    disabled={brokerSaving}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {brokerSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setEditingBrokerId(null)}
                    className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        </>)}

        {/* ═══ OVERVIEW TAB — Password Reset ═══ */}
        {activeTab === 'overview' && (<>
        {/* Password Reset */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-600" />
            Password Reset
          </h2>
          <form onSubmit={handleResetPassword} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Account</label>
              <select
                value={resetUser}
                onChange={(e) => setResetUser(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="admin">admin</option>
                <option value="broker1">broker1</option>
                <option value="dataoperator">dataoperator</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            {resetError && <p className="text-sm text-red-600">{resetError}</p>}
            {resetMsg && <p className="text-sm text-green-600">{resetMsg}</p>}
            <button
              type="submit"
              disabled={resetLoading || !newPassword}
              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
            >
              {resetLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Reset Password
            </button>
          </form>
        </div>

        </>)}

        {/* ═══ LEADS TAB ═══ */}
        {activeTab === 'leads' && (<>
        {/* Lead Stats */}
        {leadStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{leadStats.total}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Contacted Rate</p>
              <p className="text-2xl font-bold text-blue-600">{leadStats.contacted_rate.toFixed(0)}%</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Conversion Rate</p>
              <p className="text-2xl font-bold text-green-600">{leadStats.conversion_rate.toFixed(0)}%</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Revenue (Owed / Billed)</p>
              <p className="text-2xl font-bold text-purple-600">${leadStats.revenue_owed} / ${leadStats.revenue_billed}</p>
            </div>
          </div>
        )}

        {/* Lead Management */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Leads ({leads.length})
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={loadLeads} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
                <button onClick={handleExportCsv} className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm">
                  <Download className="w-4 h-4" /> CSV
                </button>
                <button onClick={handleBatchBill} className="text-purple-600 hover:text-purple-700 flex items-center gap-1 text-sm">
                  <DollarSign className="w-4 h-4" /> Bill All
                </button>
                <button onClick={handleClearAllLeads} className="text-red-600 hover:text-red-700 flex items-center gap-1 text-sm">
                  <Trash2 className="w-4 h-4" /> Clear All
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={leadFilter}
                onChange={(e) => { setLeadFilter(e.target.value); setLeadsPage(0); }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="converted">Converted</option>
                <option value="dead">Dead</option>
                <option value="waitlisted">Waitlisted</option>
              </select>
              <input
                type="date"
                value={leadDateFrom}
                onChange={(e) => setLeadDateFrom(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                placeholder="From"
              />
              <input
                type="date"
                value={leadDateTo}
                onChange={(e) => setLeadDateTo(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                placeholder="To"
              />
              {(leadFilter || leadDateFrom || leadDateTo) && (
                <button
                  onClick={() => { setLeadFilter(''); setLeadDateFrom(''); setLeadDateTo(''); setLeadsPage(0); }}
                  className="text-gray-500 hover:text-gray-700 text-sm underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
          {leadsLoading ? (
            <div className="p-8 text-center text-gray-500">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No leads yet</p>
            </div>
          ) : (
            (() => {
              // Group leads by email
              const grouped = new Map<string, Lead[]>();
              leads.forEach((lead) => {
                const key = lead.email || lead.id;
                if (!grouped.has(key)) grouped.set(key, []);
                grouped.get(key)!.push(lead);
              });
              const groups = Array.from(grouped.entries());
              const pagedGroups = groups.slice(leadsPage * LEADS_PER_PAGE, (leadsPage + 1) * LEADS_PER_PAGE);
              const toggleEmail = (email: string) => {
                setExpandedEmails((prev) => {
                  const next = new Set(prev);
                  if (next.has(email)) next.delete(email); else next.add(email);
                  return next;
                });
              };

              return (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name / Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {pagedGroups.map(([email, groupLeads]) => {
                          const isMulti = groupLeads.length > 1;
                          const isExpanded = expandedEmails.has(email);
                          const hasAccount = groupLeads.some((l) => l.converted_user_id);
                          const firstLead = groupLeads[0];

                          // Single lead — render flat row
                          if (!isMulti) return (
                            <tr key={firstLead.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1">
                                  <div className="text-sm font-medium text-gray-900">{firstLead.name || '—'}</div>
                                  {hasAccount && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-green-100 text-green-700 font-medium">Account</span>}
                                  {!hasAccount && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-500 font-medium">Guest</span>}
                                  {firstLead.is_repeat_enquiry && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 font-medium">Repeat</span>}
                                </div>
                                <div className="text-xs text-gray-500">{firstLead.email || '—'}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{firstLead.loan_purpose || '—'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {firstLead.loan_amount ? `$${Number(firstLead.loan_amount).toLocaleString()}` : '—'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  firstLead.status === 'converted' ? 'bg-green-100 text-green-800' :
                                  firstLead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                  firstLead.status === 'dead' ? 'bg-red-100 text-red-800' :
                                  firstLead.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>{firstLead.status}{firstLead.is_billed ? ' (billed)' : ''}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">${firstLead.lead_price || 50}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{firstLead.source || '—'}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {firstLead.created_at ? new Date(firstLead.created_at).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1">
                                  <select value={firstLead.status} onChange={(e) => handleLeadStatusUpdate(firstLead.id, e.target.value)}
                                    className="text-xs border border-gray-300 rounded px-1 py-0.5">
                                    <option value="new">new</option><option value="contacted">contacted</option>
                                    <option value="qualified">qualified</option><option value="converted">converted</option>
                                    <option value="dead">dead</option>
                                  </select>
                                  <button onClick={() => handleDeleteLead(firstLead.id, firstLead.name || firstLead.email || '')}
                                    className="text-red-400 hover:text-red-600 ml-1" title="Delete lead">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );

                          // Multi-lead group — collapsible
                          return (
                            <React.Fragment key={email}>
                              <tr className="hover:bg-blue-50 cursor-pointer bg-blue-50/30" onClick={() => toggleEmail(email)}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {isExpanded ? <ChevronDown className="w-4 h-4 text-blue-500" /> : <ChevronRight className="w-4 h-4 text-blue-500" />}
                                    <div className="text-sm font-medium text-gray-900">{firstLead.name || '—'}</div>
                                    <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-700 font-bold">{groupLeads.length}</span>
                                    {hasAccount && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-green-100 text-green-700 font-medium">Account</span>}
                                    {!hasAccount && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-500 font-medium">Guest</span>}
                                    {groupLeads.some(l => l.is_repeat_enquiry) && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 font-medium">Repeat</span>}
                                  </div>
                                  <div className="text-xs text-gray-500 ml-5">{email}</div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-400" colSpan={7}>
                                  {groupLeads.length} enquiries — click to {isExpanded ? 'collapse' : 'expand'}
                                </td>
                              </tr>
                              {isExpanded && groupLeads.map((lead) => (
                                <tr key={lead.id} className="hover:bg-gray-50 bg-gray-50/50">
                                  <td className="px-4 py-2 pl-10">
                                    <div className="text-xs text-gray-500 flex items-center gap-1">
                                      {lead.email}
                                      {lead.is_repeat_enquiry && <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 font-medium">Repeat</span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">{lead.loan_purpose || '—'}</td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {lead.loan_amount ? `$${Number(lead.loan_amount).toLocaleString()}` : '—'}
                                  </td>
                                  <td className="px-4 py-2">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      lead.status === 'converted' ? 'bg-green-100 text-green-800' :
                                      lead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                      lead.status === 'dead' ? 'bg-red-100 text-red-800' :
                                      lead.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>{lead.status}{lead.is_billed ? ' (billed)' : ''}</span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">${lead.lead_price || 50}</td>
                                  <td className="px-4 py-2 text-xs text-gray-500">{lead.source || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-gray-500">
                                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-1">
                                      <select value={lead.status} onChange={(e) => handleLeadStatusUpdate(lead.id, e.target.value)}
                                        className="text-xs border border-gray-300 rounded px-1 py-0.5">
                                        <option value="new">new</option><option value="contacted">contacted</option>
                                        <option value="qualified">qualified</option><option value="converted">converted</option>
                                        <option value="dead">dead</option>
                                      </select>
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id, lead.name || lead.email || ''); }}
                                        className="text-red-400 hover:text-red-600 ml-1" title="Delete lead">
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {groups.length > LEADS_PER_PAGE && (
                    <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Showing groups {leadsPage * LEADS_PER_PAGE + 1}–{Math.min((leadsPage + 1) * LEADS_PER_PAGE, groups.length)} of {groups.length} ({leads.length} total leads)
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setLeadsPage(p => Math.max(0, p - 1))}
                          disabled={leadsPage === 0}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setLeadsPage(p => Math.min(Math.ceil(groups.length / LEADS_PER_PAGE) - 1, p + 1))}
                          disabled={(leadsPage + 1) * LEADS_PER_PAGE >= groups.length}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>

        {/* Admin Config */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Lead Configuration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Master Broker (lead routing)</label>
              <div className="flex gap-2">
                <select
                  value={configMasterBroker}
                  onChange={(e) => setConfigMasterBroker(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">None selected</option>
                  {brokerList.map((b) => (
                    <option key={b.id} value={b.id}>{b.business_name || b.email}</option>
                  ))}
                </select>
                <button
                  onClick={handleSaveMasterBroker}
                  disabled={configSaving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
              {adminConfig?.master_broker_id && (
                <p className="text-xs text-gray-500 mt-1">Current: {adminConfig.master_broker_id}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Price (AUD)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={configLeadPrice}
                  onChange={(e) => setConfigLeadPrice(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  min="0"
                  step="5"
                />
                <button
                  onClick={handleSaveLeadPrice}
                  disabled={configSaving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Current: ${adminConfig?.lead_price_aud || '50'}/lead</p>
            </div>
          </div>
        </div>

        </>)}

        {/* ═══ BROKERS TAB — Connections ═══ */}
        {activeTab === 'brokers' && (<>
        {/* Broker-Customer Connections */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" />
              Broker-Customer Connections ({adminConnections.length})
            </h2>
            <div className="flex items-center gap-3">
              <select
                value={connectionStatusFilter}
                onChange={(e) => setConnectionStatusFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="pending_invite">Pending Invite</option>
                <option value="rejected">Rejected</option>
                <option value="disconnected">Disconnected</option>
              </select>
              <button onClick={loadAdminConnections} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
          </div>
          {connectionsLoading ? (
            <div className="p-8 text-center text-gray-500">Loading connections...</div>
          ) : adminConnections.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Link2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No connections yet</p>
            </div>
          ) : (() => {
            const filteredConnections = connectionStatusFilter === 'all'
              ? adminConnections
              : adminConnections.filter(c => c.status === connectionStatusFilter);
            return filteredConnections.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No connections with status "{connectionStatusFilter}"</div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Broker</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredConnections.map((c) => (
                    <tr key={c.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => loadConnectionHistory(c.id)}>
                      <td className="px-4 py-3 text-sm font-medium text-blue-700 underline">{c.broker_name || c.broker_id}</td>
                      <td className="px-4 py-3 text-sm text-blue-700 underline">{c.customer_name || c.customer_id}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          c.status === 'active' ? 'bg-green-100 text-green-800' :
                          c.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          c.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {c.status !== 'active' && (
                            <button
                              onClick={async () => {
                                const token = getAccessToken();
                                if (!token) return;
                                await updateAdminConnectionStatus(token, c.id, 'active');
                                loadAdminConnections();
                              }}
                              className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                              title="Re-enable connection"
                            >
                              Activate
                            </button>
                          )}
                          {c.status === 'active' && (
                            <button
                              onClick={async () => {
                                const token = getAccessToken();
                                if (!token) return;
                                await updateAdminConnectionStatus(token, c.id, 'disconnected');
                                loadAdminConnections();
                              }}
                              className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                              title="Disconnect"
                            >
                              Disconnect
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (!confirm(`Delete connection between ${c.broker_name || 'broker'} and ${c.customer_name || 'customer'}?`)) return;
                              const token = getAccessToken();
                              if (!token) return;
                              try {
                                await deleteAdminConnection(token, c.id);
                                loadAdminConnections();
                              } catch (err: any) {
                                alert(`Failed to delete connection: ${err?.message || 'Unknown error'}`);
                              }
                            }}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            title="Delete connection permanently"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>

        {/* Connection History Panel */}
        {(selectedConnectionHistory || connectionHistoryLoading) && (
          <div ref={connectionHistoryRef} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" />
                Connection History
                {selectedConnectionHistory && (
                  <span className="text-sm font-normal text-gray-500">
                    — {selectedConnectionHistory.connection.broker_name} &amp; {selectedConnectionHistory.connection.customer_name}
                  </span>
                )}
              </h2>
              <button onClick={() => setSelectedConnectionHistory(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {connectionHistoryLoading ? (
              <div className="p-8 text-center text-gray-500">Loading history...</div>
            ) : selectedConnectionHistory && (
              <div className="p-6 space-y-6">
                {/* Connection Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Broker</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedConnectionHistory.connection.broker_name}</p>
                    <p className="text-xs text-gray-500">{selectedConnectionHistory.connection.broker_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Customer</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedConnectionHistory.connection.customer_name}</p>
                    <p className="text-xs text-gray-500">{selectedConnectionHistory.connection.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Status</p>
                    <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                      selectedConnectionHistory.connection.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedConnectionHistory.connection.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedConnectionHistory.connection.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Connected</p>
                    <p className="text-sm text-gray-700">
                      {selectedConnectionHistory.connection.created_at
                        ? new Date(selectedConnectionHistory.connection.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </p>
                  </div>
                </div>

                {/* Delete Customer Action */}
                {selectedConnectionHistory.connection.customer_id && (
                  <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-700 flex-1">
                      Delete customer <span className="font-semibold">{selectedConnectionHistory.connection.customer_name}</span> and all their data (connections, applications, profile).
                    </p>
                    <button
                      onClick={() => setDeleteCustomerConfirm({
                        id: selectedConnectionHistory.connection.customer_id!,
                        name: selectedConnectionHistory.connection.customer_name || '—',
                        email: selectedConnectionHistory.connection.customer_email || '—',
                      })}
                      className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 flex-shrink-0"
                    >
                      Delete Customer
                    </button>
                  </div>
                )}

                {selectedConnectionHistory.connection.message && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium mb-1">Customer Message</p>
                    <p className="text-sm text-blue-800">{selectedConnectionHistory.connection.message}</p>
                  </div>
                )}

                {/* Applications */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4" />
                    Applications ({selectedConnectionHistory.applications.length})
                  </h3>
                  {selectedConnectionHistory.applications.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No applications yet</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedConnectionHistory.applications.map((app) => (
                        <div key={app.id} className={`border rounded-lg p-3 ${app.is_active ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${app.is_active ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-600'}`}>
                                {app.is_active ? 'Active' : 'Archived'}
                              </span>
                              <span className="text-sm font-medium text-gray-900">{STAGE_DISPLAY[app.stage] || app.stage}</span>
                              {app.reference_number && <span className="text-xs text-gray-500">#{app.reference_number}</span>}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              {app.lender_name && <span>{app.lender_name}</span>}
                              {app.loan_amount && <span>${app.loan_amount.toLocaleString()}</span>}
                              {app.outcome && (
                                <span className={`px-2 py-0.5 rounded-full ${
                                  app.outcome === 'approved' ? 'bg-green-100 text-green-800' :
                                  app.outcome === 'declined' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{app.outcome}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stage History */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    Stage Transitions ({selectedConnectionHistory.stage_history.length})
                  </h3>
                  {selectedConnectionHistory.stage_history.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No stage changes recorded</p>
                  ) : (
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {selectedConnectionHistory.stage_history.map((sh) => (
                        <div key={sh.id} className="flex items-center gap-2 text-xs py-1 border-b border-gray-100">
                          <span className="text-gray-400 w-28 flex-shrink-0">
                            {sh.changed_at ? new Date(sh.changed_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                          <span className="text-gray-500">{STAGE_DISPLAY[sh.from_stage || ''] || sh.from_stage || '—'}</span>
                          <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-gray-800">{STAGE_DISPLAY[sh.to_stage] || sh.to_stage}</span>
                          {sh.notes && <span className="text-gray-400 italic ml-2">{sh.notes}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Communications */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Communications ({selectedConnectionHistory.communications.length})
                  </h3>
                  {selectedConnectionHistory.communications.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No emails sent yet</p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {selectedConnectionHistory.communications.map((comm) => {
                        const templateLabels: Record<string, string> = {
                          welcome: 'Welcome Email', doc_checklist: 'Document Checklist',
                          submitted: 'Application Submitted', pre_approval: 'Pre-Approval',
                          conditional_approval: 'Conditional Approval', unconditional_approval: 'Unconditional Approval',
                          sign_documents: 'Sign Documents', settlement_approaching: 'Settlement Approaching',
                          settled_congratulations: 'Settlement Complete', declined: 'Application Declined',
                          on_hold: 'On Hold', pre_approval_expiring: 'Pre-Approval Expiring',
                          stale_alert: 'Stale Application Alert',
                        };
                        const statusColors: Record<string, string> = {
                          sent: 'bg-green-100 text-green-800',
                          pending: 'bg-yellow-100 text-yellow-800',
                          failed: 'bg-red-100 text-red-800',
                          retrying: 'bg-orange-100 text-orange-800',
                        };
                        return (
                          <div key={comm.id} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {templateLabels[comm.template_name] || comm.template_name}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[comm.status] || 'bg-gray-100 text-gray-700'}`}>
                                  {comm.status}
                                </span>
                              </div>
                              <span className="text-xs text-gray-400">
                                {comm.sent_at
                                  ? new Date(comm.sent_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                  : comm.created_at
                                  ? `queued ${new Date(comm.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                                  : '—'}
                              </span>
                            </div>
                            {comm.subject && <p className="text-xs text-gray-600 mb-1">{comm.subject}</p>}
                            <p className="text-xs text-gray-500">
                              To: {comm.recipient_email}
                              {comm.payload && typeof (comm.payload as Record<string, string>).broker_email === 'string' && (
                                <span className="ml-2">CC: {(comm.payload as Record<string, string>).broker_email}</span>
                              )}
                            </p>
                            {comm.last_error && (
                              <p className="text-xs text-red-600 mt-1">Error: {comm.last_error} (attempt {comm.attempts})</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        </>)}

        {/* ═══ DATA & SCRAPING TAB — Results ═══ */}
        {activeTab === 'scraping' && (<>
        {/* Scrape Results */}
        {scrapeResult && (
          <div className={`rounded-xl shadow-sm p-6 border mb-8 ${
            scrapeResult.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3 mb-4">
              {scrapeResult.success ? (
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-2 ${
                  scrapeResult.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  {scrapeResult.message}
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <p className="text-sm text-gray-600">Products Found</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {scrapeResult.products_found}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Valid</p>
                    <p className="text-2xl font-bold text-green-600">
                      {scrapeResult.products_valid}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Invalid</p>
                    <p className="text-2xl font-bold text-red-600">
                      {scrapeResult.products_invalid}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Quality Score</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {(scrapeResult.quality_score * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scraped Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Scraped Products</h2>
            <button
              onClick={loadScrapedProducts}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          
          {scrapedProducts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No scraped products yet</p>
              <p className="text-sm mt-1">Run a scrape to see products here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interest Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quality
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Scraped
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {scrapedProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {product.product_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          Source: {product.source}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.product_type || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {product.interest_rate?.toFixed(2)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          Comp: {product.comparison_rate?.toFixed(2)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          product.data_quality_score >= 0.8 
                            ? 'bg-green-100 text-green-800'
                            : product.data_quality_score >= 0.6
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.data_quality_score 
                            ? (product.data_quality_score * 100).toFixed(0) + '%'
                            : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.last_scraped_at 
                          ? new Date(product.last_scraped_at).toLocaleDateString()
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>)}

        {/* ═══ APPLICATIONS TAB ═══ */}
        {activeTab === 'applications' && (<>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-3">All Applications</h2>
            <div className="flex items-center gap-3">
              <input
                value={adminAppSearch}
                onChange={(e) => setAdminAppSearch(e.target.value)}
                placeholder="Search by customer, broker, or lender..."
                className="flex-1 max-w-md text-sm border border-gray-300 rounded-lg px-3 py-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadAdminApps(adminAppSearch);
                }}
              />
              <button
                onClick={() => loadAdminApps(adminAppSearch)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Search
              </button>
            </div>
          </div>

          {adminAppsLoading ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading applications...</p>
          ) : adminApps.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No applications found</p>
              <p className="text-sm mt-1">No applications exist yet, or try a different search term</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Customer</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Broker</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Lender</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Stage</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Outcome</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Created</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {adminApps.map(app => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-900">
                        {app.customer_id ? (
                          <button
                            onClick={() => setDeleteCustomerConfirm({
                              id: app.customer_id,
                              name: app.customer_name || '—',
                              email: app.customer_email || '—',
                            })}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                            title="View customer actions"
                          >
                            {app.customer_name || '—'}
                          </button>
                        ) : (
                          <span>{app.customer_name || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{app.broker_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700">{app.lender_name || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">{STAGE_DISPLAY[app.stage] || app.stage}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          app.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {app.is_active ? 'Active' : 'Archived'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          app.outcome === 'approved' ? 'bg-green-100 text-green-800'
                            : app.outcome === 'declined' ? 'bg-red-100 text-red-800'
                            : app.outcome === 'pending' ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {app.outcome || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {app.created_at ? new Date(app.created_at).toLocaleDateString('en-AU') : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => setDeleteConfirmApp(app)}
                          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmApp && (
          <>
            <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setDeleteConfirmApp(null)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Application</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to <span className="font-semibold text-red-600">permanently delete</span> this application?
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
                  <p><span className="text-gray-500">Customer:</span> <span className="font-medium">{deleteConfirmApp.customer_name || '—'}</span></p>
                  <p><span className="text-gray-500">Broker:</span> <span className="font-medium">{deleteConfirmApp.broker_name || '—'}</span></p>
                  <p><span className="text-gray-500">Stage:</span> <span className="font-medium">{STAGE_DISPLAY[deleteConfirmApp.stage] || deleteConfirmApp.stage}</span></p>
                  <p><span className="text-gray-500">Lender:</span> <span className="font-medium">{deleteConfirmApp.lender_name || '—'}</span></p>
                </div>
                <p className="text-xs text-red-600 mb-5">
                  This action cannot be undone. Stage history and conditions will be permanently deleted.
                  Audit logs and email queue entries will be preserved.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirmApp(null)}
                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setDeleting(true);
                      try {
                        const token = getAccessToken();
                        if (token) await deleteApplication(token, deleteConfirmApp.id);
                        setDeleteConfirmApp(null);
                        setAdminApps(prev => prev.filter(a => a.id !== deleteConfirmApp.id));
                      } catch (err) {
                        alert(err instanceof Error ? err.message : 'Failed to delete');
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    disabled={deleting}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete Permanently'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        </>)}

        {/* ═══ CUSTOMERS TAB ═══ */}
        {activeTab === 'customers' && (<>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-3">All Customers</h2>
            <p className="text-sm text-gray-500 mb-3">Master list of all registered customers — includes those without broker connections.</p>
            <div className="flex items-center gap-3">
              <input
                value={adminCustomerSearch}
                onChange={(e) => setAdminCustomerSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="flex-1 max-w-md text-sm border border-gray-300 rounded-lg px-3 py-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadAdminCustomers(adminCustomerSearch);
                }}
              />
              <button
                onClick={() => loadAdminCustomers(adminCustomerSearch)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Search
              </button>
              <button
                onClick={() => { setAdminCustomerSearch(''); loadAdminCustomers(); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>

          {adminCustomersLoading ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading customers...</p>
          ) : adminCustomers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No customers found</p>
              <p className="text-sm mt-1">No customers registered yet, or try a different search term</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Name</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Email</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Phone</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Connections</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Applications</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Registered</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {adminCustomers.map(cust => (
                    <tr key={cust.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{cust.full_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700">{cust.email}</td>
                      <td className="px-4 py-2.5 text-gray-700">{cust.phone_number || '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          cust.active_connections > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {cust.active_connections}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          cust.active_applications > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {cust.active_applications}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {cust.created_at ? new Date(cust.created_at).toLocaleDateString('en-AU') : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => setDeleteCustomerConfirm({
                            id: cust.id,
                            name: cust.full_name || '—',
                            email: cust.email,
                          })}
                          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>)}

      </div>

      {/* Delete Customer Confirmation Modal — rendered outside all tab blocks */}
      {deleteCustomerConfirm && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setDeleteCustomerConfirm(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Customer</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete customer <span className="font-semibold text-red-600">{deleteCustomerConfirm.name}</span>?
              </p>
              <div className="bg-red-50 rounded-lg p-3 mb-4 text-sm space-y-1">
                <p><span className="text-gray-500">Name:</span> <span className="font-medium">{deleteCustomerConfirm.name}</span></p>
                <p><span className="text-gray-500">Email:</span> <span className="font-medium">{deleteCustomerConfirm.email}</span></p>
              </div>
              <p className="text-xs text-red-600 mb-2 font-medium">This will permanently delete:</p>
              <ul className="text-xs text-red-600 mb-5 list-disc pl-4 space-y-0.5">
                <li>All broker-customer connections</li>
                <li>All loan applications (stage history, conditions)</li>
                <li>Customer profile data</li>
                <li>Active sessions and tokens</li>
              </ul>
              <p className="text-xs text-gray-500 mb-5">
                The customer account will be deactivated. If the customer logs in again, they will be treated as a new registration.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteCustomerConfirm(null)}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setDeletingCustomer(true);
                    try {
                      const token = getAccessToken();
                      if (token) {
                        const result = await deleteCustomer(token, deleteCustomerConfirm.id);
                        alert(`Customer deleted. Cleaned up: ${result.cleanup.connections} connections, ${result.cleanup.applications} applications.`);
                      }
                      setDeleteCustomerConfirm(null);
                      setSelectedConnectionHistory(null);
                      // Refresh all relevant lists
                      const token2 = getAccessToken();
                      if (token2) {
                        getAdminConnections(token2).then(data => setAdminConnections(data.connections)).catch(() => {});
                        loadAdminApps(adminAppSearch);
                        loadAdminCustomers(adminCustomerSearch);
                      }
                    } catch (err) {
                      alert(err instanceof Error ? err.message : 'Failed to delete customer');
                    } finally {
                      setDeletingCustomer(false);
                    }
                  }}
                  disabled={deletingCustomer}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingCustomer ? 'Deleting...' : 'Delete Customer'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

        {/* ═══ BILLING TAB ═══ */}
        {activeTab === 'billing' && <BillingTab token={getAccessToken()!} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BILLING TAB COMPONENT (Epic 4.1)
// ═══════════════════════════════════════════════════════════════════════════════

function BillingTab({ token }: { token: string }) {
  const [month, setMonth] = React.useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summary, setSummary] = React.useState<import('../api/brokerApi').BillingBrokerSummary[]>([]);
  const [settlements, setSettlements] = React.useState<import('../api/brokerApi').Settlement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [settlementsLoading, setSettlementsLoading] = React.useState(true);
  const [showSettlementModal, setShowSettlementModal] = React.useState(false);
  const [settlementForm, setSettlementForm] = React.useState({
    application_id: '', broker_id: '', loan_amount: '', broker_commission: '', settlement_date: '', notes: '',
  });
  const [saving, setSaving] = React.useState(false);

  // Drill-down state (#6)
  const [drillDownLeads, setDrillDownLeads] = React.useState<import('../api/brokerApi').Lead[]>([]);
  const [drillDownLoading, setDrillDownLoading] = React.useState(false);
  const [drillDownBroker, setDrillDownBroker] = React.useState<{ name: string; filter: string } | null>(null);

  // Expandable settlements state (#7)
  const [expandedSettlementId, setExpandedSettlementId] = React.useState<string | null>(null);
  const [expandedSettlementDetail, setExpandedSettlementDetail] = React.useState<import('../api/brokerApi').Settlement | null>(null);
  const [settlementDetailLoading, setSettlementDetailLoading] = React.useState(false);

  // Settlement form search state (#8)
  const [brokerOptions, setBrokerOptions] = React.useState<{ id: string; label: string }[]>([]);
  const [appOptions, setAppOptions] = React.useState<{ id: string; label: string; broker_id?: string; loan_amount?: number }[]>([]);
  const [appSearchQuery, setAppSearchQuery] = React.useState('');
  const [manualEntry, setManualEntry] = React.useState(false);

  const loadBilling = async () => {
    setLoading(true);
    try {
      const { getBillingSummary } = await import('../api/brokerApi');
      const data = await getBillingSummary(token, month);
      setSummary(data.items || []);
    } catch { setSummary([]); }
    finally { setLoading(false); }
  };

  const loadSettlements = async () => {
    setSettlementsLoading(true);
    try {
      const { listSettlements } = await import('../api/brokerApi');
      const data = await listSettlements(token, { month });
      setSettlements(data.items || []);
    } catch { setSettlements([]); }
    finally { setSettlementsLoading(false); }
  };

  React.useEffect(() => { loadBilling(); loadSettlements(); }, [month, token]);

  const handleExport = async (brokerId: string) => {
    try {
      const { exportBillingCsv } = await import('../api/brokerApi');
      const blob = await exportBillingCsv(token, brokerId, month);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `billing_${brokerId}_${month}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
  };

  const handleCreateSettlement = async () => {
    setSaving(true);
    try {
      const { createSettlement } = await import('../api/brokerApi');
      await createSettlement(token, {
        application_id: settlementForm.application_id,
        broker_id: settlementForm.broker_id,
        loan_amount: settlementForm.loan_amount ? parseFloat(settlementForm.loan_amount) : undefined,
        broker_commission: settlementForm.broker_commission ? parseFloat(settlementForm.broker_commission) : undefined,
        settlement_date: settlementForm.settlement_date || undefined,
        notes: settlementForm.notes || undefined,
      });
      setShowSettlementModal(false);
      setSettlementForm({ application_id: '', broker_id: '', loan_amount: '', broker_commission: '', settlement_date: '', notes: '' });
      loadSettlements();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create settlement');
    } finally { setSaving(false); }
  };

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      const { updateSettlementStatus } = await import('../api/brokerApi');
      await updateSettlementStatus(token, id, status);
      loadSettlements();
    } catch { alert('Failed to update status'); }
  };

  const handleDeleteSettlement = async (id: string) => {
    if (!confirm('Delete this settlement record?')) return;
    try {
      const { deleteSettlement } = await import('../api/brokerApi');
      await deleteSettlement(token, id);
      loadSettlements();
    } catch { alert('Failed to delete'); }
  };

  const handleDrillDown = async (brokerId: string, brokerName: string, filter: 'all' | 'billable') => {
    setDrillDownLoading(true);
    setDrillDownBroker({ name: brokerName, filter });
    try {
      const { getBillingLeadDetails } = await import('../api/brokerApi');
      const data = await getBillingLeadDetails(token, brokerId, month, filter);
      setDrillDownLeads(data);
    } catch { setDrillDownLeads([]); }
    finally { setDrillDownLoading(false); }
  };

  const handleExpandSettlement = async (settlementId: string) => {
    if (expandedSettlementId === settlementId) {
      setExpandedSettlementId(null);
      setExpandedSettlementDetail(null);
      return;
    }
    setExpandedSettlementId(settlementId);
    setSettlementDetailLoading(true);
    try {
      const { getSettlement } = await import('../api/brokerApi');
      const detail = await getSettlement(token, settlementId);
      setExpandedSettlementDetail(detail);
    } catch { setExpandedSettlementDetail(null); }
    finally { setSettlementDetailLoading(false); }
  };

  // Load broker options for settlement form (#8)
  const loadBrokerOptions = React.useCallback(async () => {
    try {
      const { getAdminBrokers } = await import('../api/brokerApi');
      const data = await getAdminBrokers(token);
      setBrokerOptions((data.brokers || []).map((b: import('../api/brokerApi').Broker) => ({
        id: b.id,
        label: `${b.business_name || 'No business name'} (${b.email})`,
      })));
    } catch { setBrokerOptions([]); }
  }, [token]);

  const loadAppOptions = React.useCallback(async (search?: string) => {
    try {
      const { listAllApplications } = await import('../api/brokerApi');
      const data = await listAllApplications(token, { stage: 'settled_congratulations', search });
      setAppOptions((data.applications || []).map(a => ({
        id: a.id,
        label: `${a.customer_name || 'Unknown'} — ${a.lender_name || 'N/A'} — $${Number(a.loan_amount || 0).toLocaleString()}`,
        broker_id: a.broker_id,
        loan_amount: a.loan_amount ? Number(a.loan_amount) : undefined,
      })));
    } catch { setAppOptions([]); }
  }, [token]);

  // Load broker options when settlement modal opens
  React.useEffect(() => {
    if (showSettlementModal && !manualEntry) {
      loadBrokerOptions();
      loadAppOptions();
    }
  }, [showSettlementModal, manualEntry, loadBrokerOptions, loadAppOptions]);

  const totalOutstanding = summary.reduce((s, b) => s + (b.outstanding || 0), 0);

  return (
    <div>
      {/* Month selector + summary stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Billing Summary</h2>
          <p className="text-sm text-gray-500">Per-broker lead billing and settlement commissions</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Brokers</p>
          <p className="text-2xl font-bold text-gray-900">{summary.length}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Billable Leads</p>
          <p className="text-2xl font-bold text-gray-900">{summary.reduce((s, b) => s + b.billable_leads, 0)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Outstanding Revenue</p>
          <p className="text-2xl font-bold text-green-600">${totalOutstanding.toFixed(2)}</p>
        </div>
      </div>

      {/* Broker billing table */}
      <div className="bg-white rounded-lg border mb-8">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Per-Broker Billing</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : summary.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No billing data for this month</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Broker</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Total Leads</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Billable</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Billed</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Owed</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Outstanding</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((b) => (
                  <tr key={b.broker_id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{b.business_name || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{b.broker_email}</div>
                    </td>
                    <td className="text-right px-4 py-3">
                      <button
                        onClick={() => handleDrillDown(b.broker_id, b.business_name || b.broker_email, 'all')}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                      >{b.total_leads}</button>
                    </td>
                    <td className="text-right px-4 py-3">
                      <button
                        onClick={() => handleDrillDown(b.broker_id, b.business_name || b.broker_email, 'billable')}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                      >{b.billable_leads}</button>
                    </td>
                    <td className="text-right px-4 py-3">{b.billed_leads}</td>
                    <td className="text-right px-4 py-3">${(b.total_owed || 0).toFixed(2)}</td>
                    <td className="text-right px-4 py-3 font-medium text-green-600">${(b.outstanding || 0).toFixed(2)}</td>
                    <td className="text-center px-4 py-3">
                      <button
                        onClick={() => handleExport(b.broker_id)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        <Download className="w-4 h-4 inline mr-1" />CSV
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Settlement Commissions Section */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Settlement Commissions</h3>
          <button
            onClick={() => setShowSettlementModal(true)}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            Record Settlement
          </button>
        </div>
        {settlementsLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : settlements.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No settlements recorded for this month</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Broker</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Loan Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Commission</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Platform Fee</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <React.Fragment key={s.id}>
                  <tr className="border-b hover:bg-gray-50 cursor-pointer" role="button" tabIndex={0} onClick={() => handleExpandSettlement(s.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpandSettlement(s.id); } }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {expandedSettlementId === s.id ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                        <div>
                          <div className="font-medium text-gray-900">{s.broker_name || s.broker_id.slice(0, 8)}</div>
                          {s.customer_name && <div className="text-xs text-gray-500">Customer: {s.customer_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="text-right px-4 py-3">${(s.loan_amount || 0).toLocaleString()}</td>
                    <td className="text-right px-4 py-3">${(s.broker_commission || 0).toFixed(2)}</td>
                    <td className="text-right px-4 py-3 font-medium text-green-600">${(s.platform_fee || 0).toFixed(2)}</td>
                    <td className="text-center px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={s.status}
                        onChange={(e) => handleStatusUpdate(s.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded border ${
                          s.status === 'paid' ? 'bg-green-50 text-green-700 border-green-200' :
                          s.status === 'invoiced' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          s.status === 'disputed' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-yellow-50 text-yellow-700 border-yellow-200'
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="invoiced">Invoiced</option>
                        <option value="paid">Paid</option>
                        <option value="disputed">Disputed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.settlement_date || 'N/A'}</td>
                    <td className="text-center px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDeleteSettlement(s.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {expandedSettlementId === s.id && (
                    <tr className="bg-gray-50/80">
                      <td colSpan={7} className="px-8 py-4">
                        {settlementDetailLoading ? (
                          <div className="text-sm text-gray-500">Loading details...</div>
                        ) : expandedSettlementDetail ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 block text-xs">Application ID</span>
                              <span className="font-mono text-xs text-gray-700">{expandedSettlementDetail.application_id}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs">Platform %</span>
                              <span className="text-gray-900">{expandedSettlementDetail.platform_percentage ?? 'N/A'}%</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs">Created</span>
                              <span className="text-gray-900">{expandedSettlementDetail.created_at ? new Date(expandedSettlementDetail.created_at).toLocaleString() : 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs">Updated</span>
                              <span className="text-gray-900">{expandedSettlementDetail.updated_at ? new Date(expandedSettlementDetail.updated_at).toLocaleString() : 'N/A'}</span>
                            </div>
                            {expandedSettlementDetail.notes && (
                              <div className="col-span-2">
                                <span className="text-gray-500 block text-xs">Notes</span>
                                <span className="text-gray-900">{expandedSettlementDetail.notes}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">No additional details available</div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record Settlement Modal */}
      {showSettlementModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Record Settlement Commission</h3>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={manualEntry} onChange={(e) => setManualEntry(e.target.checked)} className="rounded" />
                Manual Entry
              </label>
            </div>
            <div className="space-y-3">
              {manualEntry ? (
                <>
                  <div>
                    <label htmlFor="settle-app-id" className="block text-sm font-medium text-gray-700 mb-1">Application ID *</label>
                    <input
                      id="settle-app-id"
                      value={settlementForm.application_id}
                      onChange={(e) => setSettlementForm(f => ({ ...f, application_id: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="UUID of settled application"
                    />
                  </div>
                  <div>
                    <label htmlFor="settle-broker-id" className="block text-sm font-medium text-gray-700 mb-1">Broker ID *</label>
                    <input
                      id="settle-broker-id"
                      value={settlementForm.broker_id}
                      onChange={(e) => setSettlementForm(f => ({ ...f, broker_id: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="UUID of broker"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="settle-app-search" className="block text-sm font-medium text-gray-700 mb-1">Application *</label>
                    <input
                      id="settle-app-search"
                      type="text"
                      value={appSearchQuery}
                      onChange={(e) => { setAppSearchQuery(e.target.value); loadAppOptions(e.target.value || undefined); }}
                      className="w-full px-3 py-2 border rounded-lg text-sm mb-1"
                      placeholder="Search settled applications..."
                    />
                    <select
                      id="settle-app-select"
                      aria-label="Select application"
                      value={settlementForm.application_id}
                      onChange={(e) => {
                        const selected = appOptions.find(a => a.id === e.target.value);
                        setSettlementForm(f => ({
                          ...f,
                          application_id: e.target.value,
                          broker_id: selected?.broker_id || f.broker_id,
                          loan_amount: selected?.loan_amount ? String(selected.loan_amount) : f.loan_amount,
                        }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="">Select application...</option>
                      {appOptions.map(a => (
                        <option key={a.id} value={a.id}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="settle-broker-select" className="block text-sm font-medium text-gray-700 mb-1">Broker *</label>
                    <select
                      id="settle-broker-select"
                      value={settlementForm.broker_id}
                      onChange={(e) => setSettlementForm(f => ({ ...f, broker_id: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="">Select broker...</option>
                      {brokerOptions.map(b => (
                        <option key={b.id} value={b.id}>{b.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="settle-loan-amount" className="block text-sm font-medium text-gray-700 mb-1">Loan Amount ($)</label>
                  <input
                    id="settle-loan-amount"
                    type="number"
                    value={settlementForm.loan_amount}
                    onChange={(e) => setSettlementForm(f => ({ ...f, loan_amount: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="settle-commission" className="block text-sm font-medium text-gray-700 mb-1">Broker Commission ($)</label>
                  <input
                    id="settle-commission"
                    type="number"
                    value={settlementForm.broker_commission}
                    onChange={(e) => setSettlementForm(f => ({ ...f, broker_commission: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="settle-date" className="block text-sm font-medium text-gray-700 mb-1">Settlement Date</label>
                <input
                  id="settle-date"
                  type="date"
                  value={settlementForm.settlement_date}
                  onChange={(e) => setSettlementForm(f => ({ ...f, settlement_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label htmlFor="settle-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  id="settle-notes"
                  value={settlementForm.notes}
                  onChange={(e) => setSettlementForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSettlementModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSettlement}
                disabled={saving || !settlementForm.application_id || !settlementForm.broker_id}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Record Settlement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Drill-Down Modal (#6) */}
      {drillDownBroker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {drillDownBroker.name} — {drillDownBroker.filter === 'billable' ? 'Billable' : 'All'} Leads
              </h3>
              <button onClick={() => { setDrillDownBroker(null); setDrillDownLeads([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {drillDownLoading ? (
              <div className="p-8 text-center text-gray-500">Loading leads...</div>
            ) : drillDownLeads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No leads found</div>
            ) : (
              <div className="overflow-auto flex-1">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Name</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Email</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Purpose</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600">Loan Amt</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-600">Status</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600">Price</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Source</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {drillDownLeads.map((l) => (
                      <tr key={l.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {l.name || '—'}
                            {l.is_repeat_enquiry && <span className="px-1 py-0.5 text-[9px] rounded bg-amber-100 text-amber-700 font-medium">Repeat</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{l.email || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{l.loan_purpose || '—'}</td>
                        <td className="text-right px-3 py-2">{l.loan_amount ? `$${Number(l.loan_amount).toLocaleString()}` : '—'}</td>
                        <td className="text-center px-3 py-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            l.status === 'converted' ? 'bg-green-100 text-green-800' :
                            l.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                            l.status === 'dead' ? 'bg-red-100 text-red-800' :
                            l.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>{l.status}</span>
                        </td>
                        <td className="text-right px-3 py-2">${l.lead_price || 50}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{l.source || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-3 pt-3 border-t text-sm text-gray-500">
              {drillDownLeads.length} lead{drillDownLeads.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPortal;
